import { useMemo } from 'react'

import { useReselector } from '@/utils/sharedHooks'
import { selectHasAppAccess } from '@/selectors/user.selectors'

import { selectAnswers } from '../compliance.selectors'
import { complianceVerdict } from '../lib/qualification'
import type { DecidedVerdict } from './DisqualifiedCard'

/**
 * Where the report's closing card belongs.
 *
 * `rail` pins the next steps beside the obligations, so the way to act on the report is
 * always in reach. `end` places a decided rejection after the findings instead: pinned, it
 * would follow the reader down their whole report, which is a different thing from
 * reaching it at the end. `none` is an entitled customer, who gets no closing card at all.
 */
export type ReportEndSlot = 'none' | 'rail' | 'end'

interface ReportEndCardSlot {
  slot: ReportEndSlot
  /** Set only alongside `end`, and stable across re-renders — see below. */
  verdict: DecidedVerdict | null
}

/**
 * Splits the closing card's placement from its contents, so the report can mount it in
 * two different places without disagreeing with itself.
 *
 * Called in both `ReportView` and `ReportEndCard`, so the verdict is computed twice per
 * render. What that buys is agreement, not deduplication: the two calls read the same
 * selectors, so they cannot disagree about where the card goes and what it says.
 * Both are redux reads and a memo; nothing here fetches.
 *
 * Anything short of a decided rejection — answers still loading, questions unanswered —
 * falls through to `rail`, so a slow load can never tell a qualified practice it was
 * turned away.
 */
export const useReportEndCardSlot = (): ReportEndCardSlot => {
  const answers = useReselector(selectAnswers)
  const hasAppAccess = useReselector(selectHasAppAccess)

  // Memoized for object identity, not speed: the disqualified card records the rejection
  // in an effect keyed on this verdict, and the report re-renders on every accordion
  // toggle. A fresh object each time would re-POST the write on every toggle.
  const verdict = useMemo(() => complianceVerdict(answers), [answers])

  return useMemo(() => {
    // Only a settled `true` withholds the card — the selector is tri-state, and hiding
    // while it is still unresolved would drop the CTA for the prospects this report
    // exists to convert.
    if (hasAppAccess === true) return { slot: 'none', verdict: null }
    if (
      verdict?.treatment === 'disqualified' ||
      verdict?.treatment === 'waitlist'
    )
      return { slot: 'end', verdict }
    return { slot: 'rail', verdict: null }
  }, [hasAppAccess, verdict])
}
